<script setup lang="ts">
import axios from 'axios'

import { Loader2 } from 'lucide-vue-next'

import AppGooglePlaceIdForm from './AppGooglePlaceIdForm.vue'

import useUrl from '@/composables/url'
import useStoreUrls from '@/composables/storeUrls'

const props = defineProps<{
  storeId: number
}>()

const { user } = useLocalStorage()
const notification = useNotification()
const { hasPermissionBySlug } = useAccess()
const { saveGooglePlaceId } = useStoreUrls()
const { t } = useI18n()
const router = useRouter()
const route = useRoute()

const isLoaded = ref(false)
const isProcessing = ref(false)
const store = ref(null)
const title = ref(null)
const selectedGooglePlaceId = ref(null)
const urls = ref({
  google: null,
  facebook: null,
  instagram: null
})
const merchantSmsName = computed(() => user.value.merchant.smsName)

const fetchData = async () => {
  const { data } = await axios.get(`/stores/${props.storeId}`)

  store.value = data.data
  title.value = store.value.name
  urls.value.facebook = getUrlBySlug('facebook')
  urls.value.instagram = getUrlBySlug('instagram')
}

const getUrlBySlug = (slug: string) => {
  return store.value.urls?.find((url) => url.storeUrlCategory.slug === slug)?.url
}

const save = async () => {
  if (isProcessing.value) {
    return
  }
  isProcessing.value = true

  try {
    const requests = []

    if (selectedGooglePlaceId.value) {
      const googlePlaceIdSaved = await saveGooglePlaceId(
        props.storeId,
        selectedGooglePlaceId.value,
        store.value?.googlePlaceId
      )
      if (!googlePlaceIdSaved) {
        throw new Error('Google Place ID save failed')
      }
    }

    requests.push(
      axios.put(`stores/${props.storeId}/urls`, {
        urls: [
          {
            storeUrlCategorySlug: 'facebook',
            url: urls.value.facebook
          },
          {
            storeUrlCategorySlug: 'instagram',
            url: urls.value.instagram
          }
        ]
      })
    )

    requests.push(
      axios.patch(`stores/${props.storeId}`, {
        ...store.value
      })
    )

    await Promise.all(requests)

    notification.create('success', {
      text: t('pages.settings.storeDetailPage.notifications.update.success')
    })

    if (route.query.redirect_url) {
      router.push({
        path: useUrl().formattedRedirectUrl(route.query.redirect_url)
      })
      return
    }
    router.push({ name: 'StoreList' })
  } catch {
    notification.create('error', {
      text: t('pages.settings.storeDetailPage.notifications.update.error')
    })
  } finally {
    isProcessing.value = false
  }
}

onMounted(async () => {
  try {
    await fetchData()
  } catch {
    notification.create('error', {
      text: t('notifications.submit')
    })
  } finally {
    isLoaded.value = true
  }
})
</script>

<template>
  <div v-if="isLoaded">
    <TheDetailHeader
      :title="title"
      :parent="{ label: t('pages.settings.storesPage.title'), to: { name: 'StoreList' } }"
    />

    <form autocomplete="off" class="p-6 sm:p-12 sm:pb-12" @submit.prevent="save">
      <div class="mb-4 border bg-white p-6 sm:rounded-lg">
        <div>
          <h3 class="text-lg font-medium leading-6 text-foreground">
            {{ $t('pages.settings.storeDetailPage.config.title') }}
          </h3>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ $t('pages.settings.storeDetailPage.config.description') }}
          </p>
        </div>

        <div class="mt-6 space-y-6 sm:mt-5 sm:space-y-5">
          <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-border sm:pt-5">
            <label for="averageTicketAmount" class="block text-sm font-medium text-foreground">
              {{ $t('pages.settings.storeDetailPage.config.averageTicketAmount.title') }}
              <p class="text-xs font-normal text-muted-foreground">
                {{ $t('pages.settings.storeDetailPage.config.averageTicketAmount.description', { default: 10 }) }}
              </p>
            </label>
            <div class="mt-1 sm:col-span-2 sm:mt-0">
              <div class="flex max-w-lg rounded-md">
                <AppFormInput
                  id="averageTicketAmount"
                  v-model="store.averageTicketAmount"
                  type="number"
                  :min="0"
                  name="averageTicketAmount"
                  class="w-full"
                >
                  <template #icon-left> <span class="text-muted-foreground">€</span> </template>
                </AppFormInput>
              </div>
            </div>
          </div>

          <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
            <label for="dailyTicketCount" class="block text-sm font-medium text-foreground">
              {{ $t('pages.settings.storeDetailPage.config.dailyTicketCount.title') }}
              <p class="text-xs font-normal text-muted-foreground">
                {{ $t('pages.settings.storeDetailPage.config.dailyTicketCount.description') }}
              </p>
            </label>
            <div class="mt-1 sm:col-span-2 sm:mt-0">
              <div class="flex max-w-lg rounded-md">
                <AppFormInput
                  id="dailyTicketCount"
                  v-model="store.dailyTicketCount"
                  type="number"
                  :min="0"
                  name="dailyTicketCount"
                >
                </AppFormInput>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mb-4 border bg-white p-6 sm:rounded-lg">
        <div>
          <h3 class="text-lg font-medium leading-6 text-foreground">
            {{ $t('pages.settings.storeDetailPage.urls.title') }}
          </h3>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ $t('pages.settings.storeDetailPage.urls.description') }}
          </p>
        </div>

        <div class="mt-6 space-y-6 sm:mt-5 sm:space-y-5">
          <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:border-t sm:border-border sm:pt-5">
            <label for="name" class="block text-sm font-medium text-foreground">
              {{ t('pages.settings.storeDetailPage.urls.googlePlaceId.title') }}
              <p class="text-xs font-normal text-muted-foreground">
                {{ t('pages.settings.storeDetailPage.urls.googlePlaceId.description') }}
              </p>
            </label>
            <div class="mt-1 max-w-lg sm:col-span-2 sm:mt-0">
              <AppGooglePlaceIdForm v-model="selectedGooglePlaceId" :google-place-id="store.googlePlaceId" />
            </div>
          </div>
        </div>
        <div class="mt-6 space-y-6 sm:mt-5 sm:space-y-5">
          <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
            <label for="name" class="block text-sm font-medium text-foreground">
              {{ $t('pages.settings.storeDetailPage.urls.facebook.title') }}
              <p class="text-xs font-normal text-muted-foreground">
                {{ $t('pages.settings.storeDetailPage.urls.facebook.description') }}
              </p>
            </label>
            <div class="mt-1 sm:col-span-2 sm:mt-0">
              <div class="max-w-lg">
                <AppFormInput
                  id="facebook"
                  v-model="urls.facebook"
                  placeholder="https://www.facebook.com/*"
                  type="text"
                  name="facebook"
                >
                  <template #icon-left>
                    <BaseIcon name="IconoirFacebook" class="h-5 w-5 text-blue-600" />
                  </template>
                </AppFormInput>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-6 space-y-6 sm:mt-5 sm:space-y-5">
          <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
            <label for="name" class="block text-sm font-medium text-foreground">
              {{ $t('pages.settings.storeDetailPage.urls.instagram.title') }}
              <p class="text-xs font-normal text-muted-foreground">
                {{ $t('pages.settings.storeDetailPage.urls.instagram.description') }}
              </p>
            </label>
            <div class="mt-1 sm:col-span-2 sm:mt-0">
              <div class="max-w-lg">
                <AppFormInput
                  id="instagram"
                  v-model="urls.instagram"
                  placeholder="https://www.instagram.com/*"
                  type="text"
                  name="instagram"
                >
                  <template #icon-left>
                    <BaseIcon name="IconoirInstagram" class="h-5 w-5 text-pink-500" />
                  </template>
                </AppFormInput>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="border bg-white p-6 sm:rounded-lg">
        <div>
          <h3 class="text-lg font-medium leading-6 text-foreground">
            {{ $t('pages.settings.storeDetailPage.sms.title') }}
          </h3>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ $t('pages.settings.storeDetailPage.sms.description') }}
          </p>
        </div>

        <div class="mt-6 space-y-6 sm:mt-5 sm:space-y-5">
          <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4">
            <label for="smsName" class="block text-sm font-medium text-foreground">
              <div class="flex items-center">
                {{ $t('pages.settings.storeDetailPage.sms.sender.title') }}
                <AppTooltip tooltip-position="right" :text="t('pages.settings.merchantForm.infos.changeSenderName')">
                  <BaseIcon name="IconoirInfoCircle" class="ml-1.5 h-4 w-4 cursor-pointer text-foreground-secondary" />
                </AppTooltip>
              </div>
              <p class="text-xs font-normal text-muted-foreground">
                {{ $t('pages.settings.storeDetailPage.sms.sender.description') }}
                <strong>{{ merchantSmsName }}</strong>
              </p>
            </label>
            <div class="ml-4 mt-1 items-center sm:col-span-2 sm:mt-0">
              <div class="flex max-w-lg">
                <AppFormInput
                  id="smsName"
                  v-model="store.smsName"
                  disabled
                  type="text"
                  :min="0"
                  name="smsName"
                  class="block w-full min-w-0 flex-1 rounded-md border-input text-xs focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="hasPermissionBySlug('global-configuration.update')"
        class="mt-4 border bg-white px-4 py-5 text-center sm:rounded-lg sm:p-6"
      >
        <Button variant="default-primary" type="submit" :disabled="isProcessing">
          <Loader2 v-if="isProcessing" class="h-4 w-4 animate-spin" />
          {{ $t('global.words.save') }}
        </Button>
      </div>
    </form>
  </div>

  <TheLoadingPage v-else />
</template>
